import { useGetTinyArtworks } from "@/api/artwork/artwork";
import { useGetAllAttachments } from "@/api/attachment";
import {
  CoverPage,
  uploadCoverPageImage,
  useCreateCoverPage,
  useDeleteCoverPage,
  useGetCoverPages,
  useUpdateCoverPage,
} from "@/api/cover-page";
import { useGenerateArtworksPdf, useGenerateListTextPdf } from "@/api/pdf";
import {
  PrintModel,
  PrintTab,
  getTabsForModel,
} from "@/config/print-model-tabs.config";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import i18n, { SupportedLocale } from "@/i18n";
import {
  ARTWORK_DOCUMENT_MAIN_FACE_POSITIONS,
  getDefaultImage,
} from "@/lib/image";
import { isEmptyRichText, normalizeRichText } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { SelectValue } from "@radix-ui/react-select";
import {
  AlertCircle,
  BookMarked,
  AlertTriangle,
  Check,
  ChevronDown,
  CloudUpload,
  GripVertical,
  HelpCircle,
  Loader2,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import {
  PropsWithChildren,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PDFViewer from "../print/PDFViewer";
import PrintDrawer from "../print/PrintDrawer";
import SelectAllCheckbox from "../print/SelectAllCheckbox";
import { FieldPresenceProvider } from "@/lib/print-field-presence/FieldPresenceContext";
import { usePresentPrintFields } from "@/lib/print-field-presence/usePresentPrintFields";
import {
  PresenceLoadingHint,
  presenceLoadingClassName,
} from "@/lib/print-field-presence/PresenceLoadingHint";
import { useFieldPresenceContext } from "@/lib/print-field-presence/field-presence.context";
import SelectedRecord from "../print/SelectedRecord";
import Tiptap from "../tiptap/TipTap";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FileDropzone, FileItem } from "../ui/FileDropzone";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Sortable, SortableDragHandle, SortableItem } from "../ui/sortable";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabsContained";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { LoadImageDropzoneCta } from "../form-fields/LoadImageDropzoneCta";
import { Thumbnail } from "../ui/tumbnail";
import {
  ArtworkPrintSettings,
  PrintTemplate,
  getAllSettingsFields,
  getFieldsForTemplate,
  getSettingsGroups,
} from "./artworkPrintSettings";
import {
  hideTabbedFieldsInArtworkPrintSettings,
  isPrintSettingTabHidden,
} from "./artworkPrintSettingsVisibilityMap";
import { usePrintSettingFieldLabel } from "./usePrintSettingFieldLabel";
import { readBoPreference } from "@/lib/user-preferences/store";
import {
  getFormFieldsPreferenceKey,
  getHiddenTabIds,
  type FormFieldsVisibilityState,
} from "@/lib/form-field-visibility";

type ArtworkPrintDrawerProps = PropsWithChildren<{
  selectedArtworksIds: string[];
}>;

type FormValues = {
  template: string;
  settings: ArtworkPrintSettings;
  selectedAttachmentIds: string[];
  coverPageTitle?: string;
  coverPageParagraph1?: string;
  coverPageParagraph2?: string;
  coverImagePosition?: "header" | "after_title" | "after_para1" | "footer";
  coverImageSize?: "large" | "small";
  coverImageFile?: File | null;
  // Page layout options
  itemsPerPage?: "1" | "3";
  showTitle?: boolean;
  showPagination?: boolean;
  showPrintDate?: boolean;
  binderImageSize?: "large" | "small";
  textAlignment?: "center" | "left" | "right";
  /** Alignement du titre/header de la "Fiche Œuvre Grande Photo" (binder). */
  headerAlignment?: "center" | "left" | "right";
  imageAlignment?: "center" | "left" | "right";
  // List Text options
  showThumbnails?: boolean;
  /** Champs du résumé court (modèle "Liste d'œuvres - Vignettes"). */
  summaryFields?: {
    registrationNumber: boolean;
    artist: boolean;
    title: boolean;
    creationDate: boolean;
    dimensions: boolean;
    techniqueType: boolean;
    signature: boolean;
    status: boolean;
    price: boolean;
    storage: boolean;
    hideInches: boolean;
    hideCentimeters: boolean;
    hideRegistrationLabel: boolean;
  };
  /** Ordre d'affichage des champs du résumé (clés). */
  summaryFieldsOrder?: string[];
  listTitle?: string;
  listSubtitle?: string;
};

// Helper: Map template string to PrintModel enum
const templateToModel = (template: string): PrintModel | null => {
  switch (template) {
    case "simple":
      return PrintModel.SIMPLE;
    case "complete":
      return PrintModel.COMPLET;
    case "binder":
      return PrintModel.CLASSEUR;
    // New print type
    case "list-text":
      return PrintModel.LIST_TEXT;
    default:
      return null;
  }
};

// Helper: Map PrintTab enum to tab value strings
/**
 * Au-dela de ce nombre de champs coches, une fiche ne tient plus dans un tiers
 * de page (tickets #6 / #18). Purement indicatif : l'export reste possible.
 */
const THREE_PER_PAGE_MAX_FIELDS = 12;

const printTabToValue = (tab: PrintTab): string => {
  switch (tab) {
    case PrintTab.OPTIONS_AFFICHAGE:
      return "displayOptions";
    case PrintTab.IMAGES_SECONDAIRES:
      return "secondaryImages";
    case PrintTab.PAGE_TITRE:
      return "coverPage";
    case PrintTab.ENREGISTREMENTS_LIES:
      return "relatedRecords";
    case PrintTab.MISE_EN_PAGE:
      return "pageLayout";
    case PrintTab.PREVISUALISATION:
      return "preview";
    case PrintTab.OPTIONS_LISTE_TEXTE:
      return "listTextOptions";
    default:
      return "";
  }
};

// Helper: Map tab value to translation key
const tabValueToTranslationKey = (value: string): string => {
  switch (value) {
    case "displayOptions":
      return "printDisplayOptions";
    case "secondaryImages":
      return "printSecondaryImages";
    case "coverPage":
      return "printCoverPage";
    case "relatedRecords":
      return "printRelatedRecords";
    case "pageLayout":
      return "printPageLayout";
    case "preview":
      return "printPreview";
    case "listTextOptions":
      return "printListTextOptions";
    default:
      return "";
  }
};

const ArtworkPrintDrawerInner = ({
  selectedArtworksIds,
}: ArtworkPrintDrawerProps) => {
  const { t } = useTranslation();
  const printSettingLabel = usePrintSettingFieldLabel();
  const { fieldsForGroup, keepPresent } = usePresentPrintFields();
  const { isLoading: presenceLoading } = useFieldPresenceContext();
  const vt = useVisibleT();
  const { prismaOrderByQueryContent } = useAdvancedSearch();

  const form = useForm<FormValues>({
    defaultValues: {
      // "complete" = modèle fusionné "Fiche Œuvre Petite Photo" (superset).
      template: "complete",
      settings: getAllSettingsFields(),
      selectedAttachmentIds: [],
      coverPageTitle: "",
      coverPageParagraph1: "",
      coverPageParagraph2: "",
      coverImagePosition: "after_title",
      coverImageSize: "large",
      coverImageFile: null,
      // Page layout defaults
      itemsPerPage: "1",
      showTitle: true,
      showPagination: true,
      showPrintDate: true,
      binderImageSize: "large",
      // Défaut harmonisé : tout centré (retour client).
      textAlignment: "center",
      headerAlignment: "center",
      imageAlignment: "center",
      // List Text defaults
      showThumbnails: true,
      summaryFields: {
        registrationNumber: true,
        artist: true,
        title: true,
        creationDate: true,
        dimensions: true,
        techniqueType: true,
        signature: false,
        status: false,
        price: false,
        storage: false,
        hideInches: false,
        hideCentimeters: false,
        hideRegistrationLabel: false,
      },
      summaryFieldsOrder: [
        "registrationNumber",
        "artist",
        "title",
        "creationDate",
        "dimensions",
        "techniqueType",
        "signature",
        "status",
        "price",
        "storage",
      ],
      listTitle: "",
      listSubtitle: "",
    },
  });
  /** Reads the artwork fiche visibility grid (per-user). */
  const readArtworkVisibility = (): FormFieldsVisibilityState =>
    readBoPreference<FormFieldsVisibilityState>(
      getFormFieldsPreferenceKey("artwork"),
    ) ?? {};

  /**
   * Onglets masqués de la fiche : leurs champs sont exclus de l'impression
   * (options grisées ET sortie), toujours — indépendamment de "Basé sur les
   * champs affichés".
   */
  const hiddenArtworkTabIds = getHiddenTabIds(readArtworkVisibility());

  const { mutateAsync: generateArtworksPdf, isPending } =
    useGenerateArtworksPdf();
  const { mutateAsync: generateArtworksWord, isPending: isWordPending } =
    useGenerateArtworksPdf();
  const { mutateAsync: generateListTextPdf, isPending: isListTextPending } =
    useGenerateListTextPdf();
  const template = form.watch("template");
  const formId = useId();
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  // Cache par contexte (#9) : la config est conservée tant que la sélection
  // multi ne change pas ; on la réinitialise quand la sélection change. Le Sheet
  // est contrôlé + son contenu reste monté (keepContentMounted) pour préserver
  // les champs RHF à la fermeture.
  const previousSelectionRef = useRef<string>(selectedArtworksIds.join(","));
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Download file name provided by the API (Content-Disposition).
  const [downloadFileName, setDownloadFileName] = useState<string | undefined>(
    undefined,
  );
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saveModelName, setSaveModelName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(null);
  const [selectedCoverImageLink, setSelectedCoverImageLink] = useState<
    string | null
  >(null);
  const [existingCoverImageUrl, setExistingCoverImageUrl] = useState<
    string | null
  >(null);
  const [existingCoverImageLink, setExistingCoverImageLink] = useState<
    string | null
  >(null);

  const { instanceData } = useAuth();
  const { data: savedCovers = [] } = useGetCoverPages(template);
  const { mutateAsync: createCoverPage, isPending: isSavingCover } =
    useCreateCoverPage();
  const { mutateAsync: updateCoverPage, isPending: isUpdatingCover } =
    useUpdateCoverPage();
  const { mutateAsync: deleteCoverPage } = useDeleteCoverPage();

  const handleLoadCover = (cover: CoverPage) => {
    setSelectedCoverId(cover.id);
    setSelectedCoverImageLink(cover.imageLink ?? null);
    setExistingCoverImageUrl(cover.imageUrl ?? null);
    setExistingCoverImageLink(cover.imageLink ?? null);
    form.setValue("coverPageTitle", cover.title ?? "");
    form.setValue("coverPageParagraph1", cover.paragraph1 ?? "");
    form.setValue("coverPageParagraph2", cover.paragraph2 ?? "");
    form.setValue(
      "coverImagePosition",
      (cover.imagePosition as FormValues["coverImagePosition"]) ??
        "after_title",
    );
    form.setValue(
      "coverImageSize",
      (cover.imageSize as FormValues["coverImageSize"]) ?? "large",
    );
    // Clear any local file since we're using the saved image
    form.setValue("coverImageFile", null);
  };

  const handleDeselectCover = () => {
    setSelectedCoverId(null);
    setSelectedCoverImageLink(null);
    setExistingCoverImageUrl(null);
    setExistingCoverImageLink(null);
  };

  const handleUpdateCover = async () => {
    if (!selectedCoverId) return;
    const values = form.getValues();
    // Use null (not undefined) to explicitly remove the image in the database
    let imageLink: string | null = selectedCoverImageLink;
    const oldLink = existingCoverImageLink ?? undefined;
    if (values.coverImageFile && instanceData) {
      try {
        const attachment = await uploadCoverPageImage(
          values.coverImageFile,
          instanceData.s3Bucket,
          instanceData.id,
          oldLink,
        );
        if (attachment?.link) {
          imageLink = attachment.link;
          setSelectedCoverImageLink(attachment.link);
          setExistingCoverImageLink(attachment.link);
        }
      } catch (_) {
        // upload failed — proceed with existing imageLink
      }
    }
    await updateCoverPage({
      id: selectedCoverId,
      dto: {
        title: normalizeRichText(values.coverPageTitle),
        paragraph1: normalizeRichText(values.coverPageParagraph1),
        paragraph2: normalizeRichText(values.coverPageParagraph2),
        imageLink,
        imagePosition: values.coverImagePosition,
        imageSize: values.coverImageSize,
      },
    });
    toast({
      title: vt("coverPageUpdatedTitle", "Page de titre mise à jour"),
      description:
        vt("coverPageUpdatedDescription", "Les modifications ont été enregistrées avec succès"),
      variant: "success",
    });
  };

  const handleSaveCover = async () => {
    if (!saveModelName.trim()) return;
    const values = form.getValues();
    let imageLink: string | undefined;
    if (values.coverImageFile && instanceData) {
      const attachment = await uploadCoverPageImage(
        values.coverImageFile,
        instanceData.s3Bucket,
        instanceData.id,
      );
      imageLink = attachment.link;
      setSelectedCoverImageLink(attachment.link);
    } else if (selectedCoverImageLink) {
      imageLink = selectedCoverImageLink;
    }
    const newCover = await createCoverPage({
      name: saveModelName.trim(),
      printType: template,
      title: normalizeRichText(values.coverPageTitle),
      paragraph1: normalizeRichText(values.coverPageParagraph1),
      paragraph2: normalizeRichText(values.coverPageParagraph2),
      imageLink,
      imagePosition: values.coverImagePosition,
      imageSize: values.coverImageSize,
    });
    setSelectedCoverId(newCover.id);
    setSaveModelName("");
    setShowSaveInput(false);
  };

  // Get available tabs based on selected model
  const model = templateToModel(template);
  // Aucun enregistrement lié dans la sélection : l'onglet n'a rien à montrer.
  // Le retirer d'`availableTabs` le masque ET fait sauter « Suivant » par-dessus,
  // puisque la navigation et `isLastTab` dérivent de cette même liste.
  const hasRelatedRecords =
    fieldsForGroup(template as PrintTemplate, "relations").length > 0;
  const availableTabs = (model ? getTabsForModel(model) : []).filter(
    (tab) => tab.id !== PrintTab.ENREGISTREMENTS_LIES || hasRelatedRecords,
  );
  const [activeTab, setActiveTab] = useState<string>(
    availableTabs.length > 0
      ? printTabToValue(availableTabs[0].id)
      : "displayOptions",
  );

  // Calculate if we're on the last tab and function to go to next tab
  const availableTabValues = availableTabs.map((tab) =>
    printTabToValue(tab.id),
  );
  // L'onglet « Enregistrements liés » peut disparaître si la sélection change
  // pendant que le drawer est ouvert. Sans ce repli, on resterait sur un onglet
  // qui n'est plus rendu — panneau vide, et « Suivant » ramènerait au début
  // puisque l'index vaudrait -1.
  const tabsSignature = availableTabValues.join(",");
  useEffect(() => {
    if (availableTabValues.length === 0) return;
    if (!availableTabValues.includes(activeTab)) {
      setActiveTab(availableTabValues[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsSignature, activeTab]);

  const currentTabIndex = availableTabValues.indexOf(activeTab);
  const isLastTab = currentTabIndex === availableTabValues.length - 1;
  const isOnPreviewTab = activeTab === "preview";

  const handleNextTab = () => {
    if (currentTabIndex < availableTabValues.length - 1) {
      const nextTab = availableTabValues[currentTabIndex + 1];
      handleTabChange(nextTab);
    }
  };

  // Handle tab change - reset preview when leaving preview tab
  const handleTabChange = (newTab: string) => {
    // If leaving preview tab, reset the preview URL to force re-render on next visit
    if (activeTab === "preview" && newTab !== "preview") {
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
        setPreviewURL(null);
      }
      setPreviewError(null);
    }
    setActiveTab(newTab);
    // If navigating to preview tab, generate the PDF
    if (newTab === "preview") {
      generatePreview();
    }
  };

  useEffect(() => {
    form.setValue("settings", getAllSettingsFields());
    form.setValue("selectedAttachmentIds", []);
    setSelectedCoverId(null);
    setSelectedCoverImageLink(null);
    setExistingCoverImageUrl(null);
    setExistingCoverImageLink(null);

    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }
    setPreviewURL(null);
    setDownloadFileName(undefined);
    setPreviewError(null);
    // Reset to first tab when template changes
    if (availableTabs.length > 0) {
      const firstTabValue = printTabToValue(availableTabs[0].id);
      setActiveTab(firstTabValue);
    }
  }, [template, form]);

  // Generate PDF preview for the inline preview tab
  const generatePreview = async (forceRetry = false) => {
    if (isGeneratingPreview || (previewURL && !forceRetry)) return;

    setIsGeneratingPreview(true);
    setPreviewError(null);

    // Clear previous URL on retry
    if (forceRetry && previewURL) {
      URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
    }

    try {
      const data = form.getValues();

      let file: File;

      // Check if list-text template is selected
      if (data.template === "list-text") {
        const result = await generateListTextPdf({
          lang: i18n.language as SupportedLocale,
          artworkIds: selectedArtworksIds,
          orderBy: prismaOrderByQueryContent,
          showThumbnails: data.showThumbnails ?? true,
          summaryFields: data.summaryFields,
          summaryFieldsOrder: data.summaryFieldsOrder,
          showTitle: data.showTitle,
          showPagination: data.showPagination,
          showPrintDate: data.showPrintDate,
          listTitle: data.listTitle,
          listSubtitle: data.listSubtitle,
        });
        file = result.file;
        setDownloadFileName(result.filename);
      } else {
        // Same params as the download path (incl. inline cover page and the
        // visibility-grid filtering), so the preview matches the exported
        // document exactly.
        const params = await getFormDataForPrint();
        const result = await generateArtworksPdf(params);
        file = result.file;
        setDownloadFileName(result.filename);
      }

      const url = URL.createObjectURL(file);
      setPreviewURL(url);
    } catch (error) {
      console.error("Failed to generate preview:", error);
      setPreviewError(
        vt("previewGenerationError", "Erreur lors de la génération de l'aperçu"),
      );
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Couverture saisie dans le drawer mais pas (encore) enregistrée comme modèle.
  // Doit rester aligné sur `hasInlineCover` dans getFormDataForPrint, sinon le
  // drawer annonce une couverture qui ne s'imprime pas (ou l'inverse).
  const watchedCoverTitle = form.watch("coverPageTitle");
  const watchedCoverParagraph1 = form.watch("coverPageParagraph1");
  const watchedCoverParagraph2 = form.watch("coverPageParagraph2");
  const watchedCoverImageFile = form.watch("coverImageFile");
  const hasUnsavedCover =
    !selectedCoverId &&
    (!isEmptyRichText(watchedCoverTitle) ||
      !isEmptyRichText(watchedCoverParagraph1) ||
      !isEmptyRichText(watchedCoverParagraph2) ||
      !!watchedCoverImageFile ||
      !!selectedCoverImageLink);

  /**
   * Shared helper: builds the full params for PDF/Word generation.
   * Handles cover image upload, secondary images flag, and all layout options.
   * Used by both onSubmit (PDF preview) and handleDownloadWord.
   */
  const getFormDataForPrint = async () => {
    const data = form.getValues();
    // Handle cover image upload if needed
    let coverImageLink = selectedCoverImageLink ?? undefined;
    if (
      !selectedCoverId &&
      data.coverImageFile &&
      !coverImageLink &&
      instanceData
    ) {
      const attachment = await uploadCoverPageImage(
        data.coverImageFile,
        instanceData.s3Bucket,
        instanceData.id,
      );
      coverImageLink = attachment.link;
      setSelectedCoverImageLink(attachment.link);
    }
    // Set showSecondaryImages to true if user has selected any secondary images
    let settings = {
      ...data.settings,
      showSecondaryImages: data.selectedAttachmentIds.length > 0,
    };
    // Un onglet masqué dans la fiche ne s'imprime jamais (toujours appliqué).
    settings = hideTabbedFieldsInArtworkPrintSettings(
      settings,
      hiddenArtworkTabIds,
    );
    // Un réglage sans valeur dans la sélection n'a pas été proposé : il ne doit
    // pas repartir à true parce qu'il l'était avant que la sélection change.
    for (const key of Object.keys(settings)) {
      if (!keepPresent(key)) {
        settings[key as keyof ArtworkPrintSettings] = false;
      }
    }
    // Inline cover page: when no saved model is selected but the user filled the
    // cover tab (title / paragraph / image), send the raw fields so the cover
    // renders without requiring a prior save — same contract as the
    // single-artwork drawer.
    // `isEmptyRichText` et non `.trim()` : un éditeur TipTap vidé renvoie
    // `<p></p>`, qui ferait sortir une page de couverture blanche.
    const hasInlineCover =
      !selectedCoverId &&
      (!isEmptyRichText(data.coverPageTitle) ||
        !isEmptyRichText(data.coverPageParagraph1) ||
        !isEmptyRichText(data.coverPageParagraph2) ||
        !!coverImageLink);
    return {
      lang: i18n.language as SupportedLocale,
      template: data.template,
      artworkIds: selectedArtworksIds,
      settings,
      orderBy: prismaOrderByQueryContent,
      selectedAttachmentIds: data.selectedAttachmentIds,
      coverPageId: selectedCoverId ?? undefined,
      coverPage: hasInlineCover
        ? {
            title: normalizeRichText(data.coverPageTitle),
            paragraph1: normalizeRichText(data.coverPageParagraph1),
            paragraph2: normalizeRichText(data.coverPageParagraph2),
            imageLink: coverImageLink ?? null,
            imageSize: data.coverImageSize ?? "large",
            imagePosition: data.coverImagePosition ?? "after_title",
          }
        : undefined,
      // Page layout options
      itemsPerPage: data.itemsPerPage,
      showTitle: data.showTitle,
      showPagination: data.showPagination,
      showPrintDate: data.showPrintDate,
      listTitle: data.listTitle,
      binderImageSize: data.binderImageSize,
      textAlignment: data.template === "binder" ? data.textAlignment : "left",
      headerAlignment:
        data.template === "binder" ? data.headerAlignment : undefined,
      imageAlignment:
        data.template === "binder" ? data.imageAlignment : undefined,
    };
  };

  /**
   * Shared helper for list-text params.
   */
  const getListTextParams = (format?: "word") => {
    const data = form.getValues();
    return {
      lang: i18n.language as SupportedLocale,
      artworkIds: selectedArtworksIds,
      orderBy: prismaOrderByQueryContent,
      showThumbnails: data.showThumbnails ?? true,
      summaryFields: data.summaryFields,
      summaryFieldsOrder: data.summaryFieldsOrder,
      showTitle: data.showTitle,
      showPagination: data.showPagination,
      showPrintDate: data.showPrintDate,
      listTitle: data.listTitle,
      listSubtitle: data.listSubtitle,
      ...(format ? { format } : {}),
    };
  };

  const onSubmit = async (data: FormValues) => {
    let file: File;

    if (data.template === "list-text") {
      const result = await generateListTextPdf(getListTextParams());
      file = result.file;
      setDownloadFileName(result.filename);
    } else {
      const params = await getFormDataForPrint();
      const result = await generateArtworksPdf(params);
      file = result.file;
      setDownloadFileName(result.filename);
    }

    const url = URL.createObjectURL(file);
    setPreviewURL(url);
  };

  const handleDownloadWord = async () => {
    const data = form.getValues();
    // Indicateur de progression : le .docx peut être long à générer (retour client).
    const progress = toast({
      title: vt("wordExportInProgressTitle", "Génération du document Word…"),
      description: vt(
        "wordExportInProgressDescription",
        "Merci de patienter, le téléchargement démarrera automatiquement.",
      ),
    });
    try {
      let file: File;
      let name: string | undefined;
      if (data.template === "list-text") {
        const result = await generateListTextPdf(getListTextParams("word"));
        file = result.file;
        name = result.filename;
      } else {
        const params = await getFormDataForPrint();
        const result = await generateArtworksWord({
          ...params,
          format: "word",
        });
        file = result.file;
        name = result.filename;
      }
      // Download the Word file
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = name ?? "Artworks.docx";
      link.click();
      URL.revokeObjectURL(url);
      progress.dismiss();
      toast({
        title: vt("wordExportDoneTitle", "Document Word prêt"),
        variant: "success",
      });
    } catch (error) {
      progress.dismiss();
      toast({
        title: vt("wordExportErrorTitle", "Échec de la génération du document Word"),
        description: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
    }
  };

  const { data: selectedArtworks = [] } = useGetTinyArtworks({
    where: {
      id: { in: selectedArtworksIds },
    },
    orderBy: prismaOrderByQueryContent,
  });

  // Get secondary attachments for selected artworks (exclude main face image).
  // Les œuvres du module sculpture rangent leur face sous
  // `sculptureEditionDescriptionPhotoFace` : sans elle dans le `notIn`, la face
  // réapparaît dans le sélecteur d'images secondaires.
  const { data: allAttachments = [] } = useGetAllAttachments({
    where: {
      docId: { in: selectedArtworksIds },
      position: { notIn: [...ARTWORK_DOCUMENT_MAIN_FACE_POSITIONS] },
    },
    order: { order: "asc" },
  });

  // Watch selected attachment IDs
  const selectedAttachmentIds = form.watch("selectedAttachmentIds");

  // Tickets #6 / #18 : en 3 fiches par page, une fiche trop fournie deborde son
  // tiers de page et decale toute la mise en page. On previent l'utilisateur au
  // moment ou il choisit la mise en page, plutot que de le laisser decouvrir le
  // resultat dans le PDF.
  const watchedSettings = form.watch("settings");
  const watchedItemsPerPage = form.watch("itemsPerPage");
  const watchedTemplate = form.watch("template");

  const hasThreePerPageOverflowRisk = useMemo(() => {
    if (watchedItemsPerPage !== "3") return false;
    const settings = (watchedSettings ?? {}) as Record<string, unknown>;

    // Blocs pleine largeur : un seul suffit a faire deborder un tiers de page.
    const heavyBlocks = [
      "showLongDescription",
      "oeuvresValidationRemarquesPubliques",
      "showSecondaryImages",
      ...getFieldsForTemplate(watchedTemplate as PrintTemplate, "relations"),
    ];
    if (heavyBlocks.some((field) => settings[field] === true)) return true;

    const checkedCount = Object.values(settings).filter(
      (value) => value === true,
    ).length;
    return checkedCount > THREE_PER_PAGE_MAX_FIELDS;
  }, [watchedItemsPerPage, watchedSettings, watchedTemplate]);

  const registrationNumberByArtworkId = new Map(
    selectedArtworks.map((a) => [
      a.id,
      a.oeuvresDescriptionNumeroEnregistrement,
    ]),
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{
            isLoading: isPending || isGeneratingPreview || isListTextPending,
          }}
          isLastTab={isLastTab}
          isOnPreviewTab={isOnPreviewTab}
          onNextTab={handleNextTab}
          open={drawerOpen}
          keepContentMounted
          onOpenChange={(open) => {
            setDrawerOpen(open);
            if (!open) return;
            // À l'ouverture : toujours régénérer la prévisu à neuf.
            if (previewURL) URL.revokeObjectURL(previewURL);
            setPreviewURL(null);
            setDownloadFileName(undefined);
            setPreviewError(null);
            if (availableTabs.length > 0) {
              setActiveTab(printTabToValue(availableTabs[0].id));
            }
            // Cache par contexte (#9) : ne réinitialiser la config que si la
            // sélection multi a changé depuis la dernière ouverture.
            const selectionKey = selectedArtworksIds.join(",");
            if (selectionKey !== previousSelectionRef.current) {
              previousSelectionRef.current = selectionKey;
              setSelectedCoverId(null);
              setSelectedCoverImageLink(null);
              setExistingCoverImageUrl(null);
              setExistingCoverImageLink(null);
              setSaveModelName("");
              setShowSaveInput(false);
              form.reset();
            }
          }}
          previewFooter={
            <div className="flex flex-row flex-1 items-center gap-4 p-4">
              <Button
                type="button"
                variant="outline"
                className="flex flex-1 bg-white"
                onClick={() => {
                  setPreviewURL(null);
                  if (availableTabs.length > 0) {
                    setActiveTab(printTabToValue(availableTabs[0].id));
                  }
                }}
              >
                {t("actionCancel")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={
                      !previewURL || isGeneratingPreview || isWordPending
                    }
                    className="flex flex-1 items-center justify-center gap-2"
                  >
                    {isWordPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {vt("download", "Télécharger")}
                        <ChevronDown className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      if (previewURL) {
                        const link = document.createElement("a");
                        link.href = previewURL;
                        link.download = downloadFileName ?? "Artworks.pdf";
                        link.click();
                      }
                    }}
                  >
                    {vt("downloadPdf", "PDF")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDownloadWord}
                    disabled={isWordPending}
                  >
                    {isWordPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {vt("downloadWord", "Word (.docx)")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        >
          <div className="flex flex-col gap-4 items-center">
            <h3 className="self-start text-sm font-bold uppercase tracking-wide text-[#008BD4]">
              {vt("printArtworksToExportTitle", "Liste des œuvres à exporter")}
            </h3>
            <div className="grid grid-cols-3 gap-3 w-full max-h-[120px] overflow-y-auto pr-2">
              {selectedArtworks.map(
                ({
                  id,
                  attachments,
                  oeuvresDescriptionTitre,
                  oeuvresDescriptionNumeroEnregistrement,
                }) => {
                  const image = getDefaultImage(
                    attachments,
                    "artwork",
                    "publicUrl",
                  );

                  return (
                    <SelectedRecord
                      key={id}
                      image={image}
                      title={[
                        oeuvresDescriptionTitre,
                        oeuvresDescriptionNumeroEnregistrement,
                      ]
                        .filter(Boolean)
                        .join(" - ")}
                    />
                  );
                },
              )}
            </div>
          </div>

          <Separator className="my-6" />

          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                    {t("printSelectModel")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="complete">
                        {vt("bo.print.modelSmallPhoto", "Fiche Œuvre Petite Photo")}
                      </SelectItem>
                      <SelectItem value="binder">
                        {vt("bo.print.modelLargePhoto", "Fiche Œuvre Grande Photo")}
                      </SelectItem>
                      <SelectItem value="list-text">
                        {vt("bo.print.modelVignettes", "Liste d'œuvres - Vignettes")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <Tabs
              defaultValue="displayOptions"
              className="w-full flex flex-col items-center"
              value={activeTab}
              onValueChange={handleTabChange}
            >
              <TabsList className="h-9">
                {availableTabs.map((tab, index) => {
                  const tabValue = printTabToValue(tab.id);
                  const isActive = activeTab === tabValue;
                  const isDisabled = index > currentTabIndex;

                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tabValue}
                      disabled={isDisabled}
                      className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 ${
                        !isActive ? "bg-slate-100" : ""
                      } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <Badge
                        variant={isActive ? "outlinedActive" : "outlined"}
                        className="h-4 min-w-4 text-[10px] px-1 flex items-center justify-center leading-none"
                      >
                        {index + 1}
                      </Badge>
                      {t(tabValueToTranslationKey(tabValue))}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <TabsContent value="displayOptions" className="mt-4 w-full">
                <div className="w-full">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                    {vt("printDisplayOptionsTitle", "Options d'affichage")}
                  </h3>
                  <SelectAllCheckbox
                    form={form}
                    template={template as PrintTemplate}
                  />
                  {/* Conteneur positionné : l'indicateur de calcul se superpose aux
                      cases plutôt que d'occuper une ligne au-dessus. */}
                  <div className="relative">
                    <PresenceLoadingHint />
                    <div
                      className={`grid grid-cols-2 gap-x-8 gap-y-0 ${presenceLoadingClassName(
                        presenceLoading,
                      )}`}
                      aria-busy={presenceLoading}
                    >
                      {/* Left column */}
                      <div>
                        {Object.entries(
                          getSettingsGroups(template as PrintTemplate),
                        )
                          .filter(([, group]) => group.column === "left")
                          .map(([groupKey, group]) => {
                            const fieldsInGroup = fieldsForGroup(
                              template as PrintTemplate,
                              groupKey,
                            ).filter(
                              (field) =>
                                !isPrintSettingTabHidden(
                                  field as keyof ArtworkPrintSettings,
                                  hiddenArtworkTabIds,
                                ),
                            );
                            if (fieldsInGroup.length === 0) return null;
                            return (
                              <div key={group.key} className="mb-6">
                                <h3 className="text-sm font-semibold mb-3">
                                  {t(group.titleKey)}
                                </h3>
                                <div className="space-y-2">
                                  {fieldsInGroup.map((field) => (
                                    <FormField
                                      key={field}
                                      control={form.control}
                                      name={`settings.${field}` as any}
                                      render={({ field: formField }) => (
                                        <FormItem className="flex flex-row gap-2 space-y-0">
                                          <FormControl>
                                            <Checkbox
                                              checked={formField.value as boolean}
                                              onCheckedChange={formField.onChange}
                                            />
                                          </FormControl>
                                          <FormLabel className="text-xs font-normal">
                                            {printSettingLabel(field)}
                                          </FormLabel>
                                        </FormItem>
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      {/* Right column */}
                      <div>
                        {Object.entries(
                          getSettingsGroups(template as PrintTemplate),
                        )
                          .filter(
                            ([groupKey, group]) =>
                              group.column === "right" &&
                              groupKey !== "relations",
                          )
                          .map(([groupKey, group]) => {
                            const fieldsInGroup = fieldsForGroup(
                              template as PrintTemplate,
                              groupKey,
                            ).filter(
                              (field) =>
                                !isPrintSettingTabHidden(
                                  field as keyof ArtworkPrintSettings,
                                  hiddenArtworkTabIds,
                                ),
                            );
                            if (fieldsInGroup.length === 0) return null;
                            return (
                              <div key={group.key} className="mb-6">
                                <h3 className="text-sm font-semibold mb-3">
                                  {t(group.titleKey)}
                                </h3>
                                <div className="space-y-2">
                                  {fieldsInGroup.map((field) => (
                                    <FormField
                                      key={field}
                                      control={form.control}
                                      name={`settings.${field}` as any}
                                      render={({ field: formField }) => (
                                        <FormItem className="flex flex-row gap-2 space-y-0">
                                          <FormControl>
                                            <Checkbox
                                              checked={formField.value as boolean}
                                              onCheckedChange={formField.onChange}
                                            />
                                          </FormControl>
                                          <FormLabel className="text-xs font-normal">
                                            {printSettingLabel(field)}
                                          </FormLabel>
                                        </FormItem>
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="relatedRecords" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {t("printRelatedRecords")}
                </h3>
                {(() => {
                  const relationFields = fieldsForGroup(
                    template as PrintTemplate,
                    "relations",
                  );
                  const currentSettings = form.watch("settings");
                  const allChecked =
                    relationFields.length > 0 &&
                    relationFields.every(
                      (f) => currentSettings?.[f as keyof typeof currentSettings],
                    );
                  return (
                    <div className="flex items-center justify-start gap-2 mb-4">
                      <Checkbox
                        checked={allChecked}
                        onCheckedChange={(checked) => {
                          const next = { ...currentSettings };
                          relationFields.forEach((f) => {
                            next[f as keyof typeof next] = !!checked as never;
                          });
                          form.setValue("settings", next);
                        }}
                      />
                      <label
                        className="text-sm font-normal cursor-pointer"
                        style={{ color: "#008BD4" }}
                      >
                        {vt("selectAll", "Tout cocher")}
                      </label>
                    </div>
                  );
                })()}
                <div className="space-y-2 w-fit">
                  {fieldsForGroup(
                    template as PrintTemplate,
                    "relations",
                  ).map((field) => (
                    <FormField
                      key={field}
                      control={form.control}
                      name={`settings.${field}` as any}
                      render={({ field: formField }) => (
                        <FormItem className="flex flex-row items-start gap-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={formField.value as boolean}
                              onCheckedChange={formField.onChange}
                            />
                          </FormControl>
                          <FormLabel className="text-xs font-normal">
                            {printSettingLabel(field)}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="secondaryImages" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("printSecondaryImages", "Images secondaires")}
                </h3>
                {allAttachments.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    {t("noSecondaryImages")}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-start gap-2 mb-4">
                      <Checkbox
                        checked={
                          selectedAttachmentIds.length === allAttachments.length
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            form.setValue(
                              "selectedAttachmentIds",
                              allAttachments.map((a) => a.id),
                            );
                          } else {
                            form.setValue("selectedAttachmentIds", []);
                          }
                        }}
                      />
                      <label
                        className="text-sm font-normal cursor-pointer"
                        style={{ color: "#008BD4" }}
                      >
                        {t("selectAll")}
                      </label>
                    </div>
                    <div className="grid grid-cols-6 gap-3">
                      {allAttachments.map((attachment) => {
                        const isSelected = selectedAttachmentIds.includes(
                          attachment.id,
                        );
                        const registrationNumber =
                          registrationNumberByArtworkId.get(attachment.docId);

                        return (
                          <div
                            key={attachment.id}
                            className="flex flex-col gap-1"
                          >
                            <div
                              className="relative aspect-square rounded-md overflow-hidden border-2 transition-all cursor-pointer hover:border-primary"
                              style={{
                                borderColor: isSelected
                                  ? "hsl(var(--primary))"
                                  : "hsl(var(--border))",
                              }}
                              onClick={() => {
                                const currentIds = form.getValues(
                                  "selectedAttachmentIds",
                                );
                                if (isSelected) {
                                  form.setValue(
                                    "selectedAttachmentIds",
                                    currentIds.filter(
                                      (id) => id !== attachment.id,
                                    ),
                                  );
                                } else {
                                  form.setValue("selectedAttachmentIds", [
                                    ...currentIds,
                                    attachment.id,
                                  ]);
                                }
                              }}
                            >
                              <img
                                src={
                                  attachment.mediumPublicUrl ||
                                  attachment.smallPublicUrl ||
                                  ""
                                }
                                alt={
                                  attachment.textAlt?.[
                                    i18n.language as SupportedLocale
                                  ] || ""
                                }
                                className="w-full h-full object-cover"
                              />
                              <div
                                className="absolute top-2 right-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    const currentIds = form.getValues(
                                      "selectedAttachmentIds",
                                    );
                                    const alreadySelected = currentIds.includes(
                                      attachment.id,
                                    );
                                    if (checked && !alreadySelected) {
                                      form.setValue("selectedAttachmentIds", [
                                        ...currentIds,
                                        attachment.id,
                                      ]);
                                    } else if (
                                      checked === false &&
                                      alreadySelected
                                    ) {
                                      form.setValue(
                                        "selectedAttachmentIds",
                                        currentIds.filter(
                                          (id) => id !== attachment.id,
                                        ),
                                      );
                                    }
                                  }}
                                  className="bg-white"
                                />
                              </div>
                            </div>
                            {/* Retour client : les vignettes portaient toutes le
                                meme numero d'enregistrement, la position est ce
                                qui les distingue. */}
                            {attachment.position && (
                              <span className="text-xs text-center font-medium truncate">
                                {t(attachment.position)}
                              </span>
                            )}
                            {registrationNumber && (
                              <span className="text-xs text-center text-muted-foreground truncate">
                                {registrationNumber}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="coverPage" className="mt-4 w-full">
                <div className="space-y-6 px-8">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                    {vt("printCoverPageTitle", "Page titre")}
                  </h3>

                  {/* Saved covers list */}
                  {savedCovers.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BookMarked className="size-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">
                          {t("coverPageSavedModels")}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {savedCovers.map((cover) => {
                          const isSelected = selectedCoverId === cover.id;
                          return (
                            <div
                              key={cover.id}
                              onClick={() =>
                                isSelected
                                  ? handleDeselectCover()
                                  : handleLoadCover(cover)
                              }
                              className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border text-xs font-medium cursor-pointer transition-all select-none ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50 text-blue-700"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {isSelected && (
                                <Check className="size-3 text-blue-600 shrink-0" />
                              )}
                              <span>{cover.name}</span>
                              <button
                                type="button"
                                className={`ml-0.5 rounded-full p-0.5 transition-colors ${
                                  isSelected
                                    ? "text-blue-400 hover:text-blue-700 hover:bg-blue-100"
                                    : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) handleDeselectCover();
                                  deleteCoverPage(cover.id);
                                }}
                              >
                                <X className="size-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <Separator className="mt-4" />
                      <p className="text-sm font-semibold mt-4">
                        {t(
                          selectedCoverId
                            ? "editCoverPageModel"
                            : "addCoverPageModel",
                        )}
                      </p>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="coverPageTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold">
                          {t("coverPageTitle")}
                        </FormLabel>
                        <FormControl>
                          <Tiptap
                            {...field}
                            label=""
                            description={field.value || ""}
                            onChange={field.onChange}
                            defaultTextAlign="center"
                            toolbarPreset="complete"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="coverPageParagraph1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold">
                          {t("coverPageParagraph1")}
                        </FormLabel>
                        <FormControl>
                          <Tiptap
                            {...field}
                            label=""
                            description={field.value || ""}
                            onChange={field.onChange}
                            defaultTextAlign="center"
                            toolbarPreset="complete"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="coverPageParagraph2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold">
                          {t("coverPageParagraph2")}
                        </FormLabel>
                        <FormControl>
                          <Tiptap
                            {...field}
                            label=""
                            description={field.value || ""}
                            onChange={field.onChange}
                            defaultTextAlign="center"
                            toolbarPreset="complete"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Separator className="my-6" />

                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-4">
                      {t("coverImageTitle")}
                    </h4>
                    <div className="flex gap-6">
                      <div className="flex-1">
                        <FormField
                          control={form.control}
                          name="coverImagePosition"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">
                                {t("coverImagePosition")}
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue
                                      placeholder={t("selectPosition")}
                                    />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="header">
                                    {vt(
                                      "bo.print.coverImagePositionHeader",
                                      "Avant le titre",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="after_title">
                                    {vt(
                                      "bo.print.coverImagePositionAfterTitle",
                                      "Après le titre",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="after_para1">
                                    {vt(
                                      "bo.print.coverImagePositionAfterPara1",
                                      "Après le paragraphe 1",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="footer">
                                    {vt(
                                      "bo.print.coverImagePositionFooter",
                                      "Après le paragraphe 2",
                                    )}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <FormField
                          control={form.control}
                          name="coverImageSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">
                                {t("coverImageSize")}
                              </FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex flex-row gap-4 mt-4"
                                >
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="large" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {t("coverImageSizeLarge")}
                                    </FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="small" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {t("coverImageSizeSmall")}
                                    </FormLabel>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <FormField
                        control={form.control}
                        name="coverImageFile"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              {field.value ? (
                                // Local file selected
                                <FileItem
                                  file={field.value}
                                  image={
                                    <img
                                      src={URL.createObjectURL(field.value)}
                                      alt="preview"
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  }
                                  onDelete={() => {
                                    field.onChange(null);
                                    setSelectedCoverImageLink(null);
                                    setExistingCoverImageUrl(null);
                                  }}
                                />
                              ) : existingCoverImageUrl ? (
                                // Image from saved cover model
                                <div className="flex items-center gap-3 p-3 border rounded-md">
                                  <img
                                    src={existingCoverImageUrl}
                                    alt="cover"
                                    className="w-10 h-10 object-cover rounded"
                                  />
                                  <span className="text-sm text-muted-foreground flex-1">
                                    {t("coverImageFromModel")}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCoverImageLink(null);
                                      setExistingCoverImageUrl(null);
                                    }}
                                  >
                                    ×
                                  </Button>
                                </div>
                              ) : (
                                // No image — show dropzone
                                <FileDropzone
                                  onDrop={(files) => {
                                    if (files.length > 0) {
                                      field.onChange(files[0]);
                                      setExistingCoverImageUrl(null);
                                      setSelectedCoverImageLink(null);
                                    }
                                  }}
                                  multiple={false}
                                  className="border-solid"
                                >
                                  {() => (
                                    <div className="flex flex-col items-center justify-center gap-6">
                                      <Thumbnail>
                                        <CloudUpload className="text-gray-700 size-6" />
                                      </Thumbnail>
                                      <LoadImageDropzoneCta dragCtaKey="loadImage.dragCtaSingle" />
                                    </div>
                                  )}
                                </FileDropzone>
                              )}
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <Separator className="my-6" />
                  {/* Rappel : couverture saisie mais non enregistrée comme modèle (#13) */}
                  {(() => {
                    if (!hasUnsavedCover || showSaveInput) return null;
                    return (
                      <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                        <span>
                          {vt(
                            "coverPageUnsavedHint",
                            "Cette page de couverture n'est pas enregistrée comme modèle. Enregistrez-la pour la réutiliser (elle s'imprimera même sans être enregistrée).",
                          )}
                        </span>
                      </div>
                    );
                  })()}
                  {/* Save as model */}
                  <div className="flex justify-end">
                    {selectedCoverId ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleUpdateCover}
                        disabled={isUpdatingCover}
                      >
                        <Save className="size-3.5 mr-1.5" />
                        {t("saveCoverPageChanges")}
                      </Button>
                    ) : showSaveInput ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={saveModelName}
                          onChange={(e) => setSaveModelName(e.target.value)}
                          placeholder={t("coverPageModelName")}
                          className="flex-1 text-sm"
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleSaveCover()
                          }
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveCover}
                          disabled={!saveModelName.trim() || isSavingCover}
                        >
                          <Save className="size-3.5 mr-1.5" />
                          {t("save")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowSaveInput(false);
                            setSaveModelName("");
                          }}
                        >
                          {t("cancel")}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSaveInput(true)}
                      >
                        <Save className="size-3.5 mr-1.5" />
                        {t("coverPageSaveAsModel")}
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pageLayout" className="mt-4 w-full">
                {/* Modèle liste : pas d'indent, la rubrique s'aligne à gauche
                    comme les autres (retour client). */}
                <div
                  className={`space-y-6 ${
                    template === "list-text" ? "" : "px-8"
                  }`}
                >
                  {template === "binder" ? (
                    <>
                      {/* Binder-specific page layout: image size + text alignment as radio groups */}
                      <FormField
                        control={form.control}
                        name="binderImageSize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("binderImageSize", "Taille de l'image")}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-row gap-6 mt-2"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="large" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("binderImageSizeLarge", "Grande")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="small" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("binderImageSizeSmall", "Petite")}
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <Separator />

                      <FormField
                        control={form.control}
                        name="textAlignment"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("textAlignment", "Alignement du texte")}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-row gap-6 mt-2"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="center" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignCenter", "Centré")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="left" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignLeftFull", "Aligné à gauche")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="right" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignRightFull", "Aligné à droite")}
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <Separator />

                      <FormField
                        control={form.control}
                        name="headerAlignment"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("headerAlignment", "Alignement du header")}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-row gap-6 mt-2"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="center" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignCenter", "Centré")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="left" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignLeftFull", "Aligné à gauche")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="right" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignRightFull", "Aligné à droite")}
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Separator />

                      {/* Ticket #13 : alignement dedie de l'image principale,
                          independant du texte et de l'en-tete. */}
                      <FormField
                        control={form.control}
                        name="imageAlignment"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("imageAlignment", "Alignement de l'image")}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-row gap-6 mt-2"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="center" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignCenter", "Centré")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="left" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignLeftFull", "Aligné à gauche")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="right" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("alignRightFull", "Aligné à droite")}
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </>
                  ) : (
                    <>
                      {/* Non-binder page layout */}
                      <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                        {template === "list-text"
                          ? vt("printListExportOptionsTitle", "Options d'export")
                          : vt("printPageLayoutTitle", "Mise en page")}
                      </h3>

                      {/* Items per page - for complete and simple templates, only when multiple artworks selected */}
                      {(template === "complete" || template === "simple") &&
                        selectedArtworksIds.length > 1 && (
                          <FormField
                            control={form.control}
                            name="itemsPerPage"
                            render={({ field }) => (
                              <FormItem className="w-1/3">
                                <FormLabel className="text-sm font-medium flex items-center gap-1">
                                  {vt("itemsPerPage", "Nombre de fiches par page")}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="size-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        {vt("itemsPerPageTooltip", "Définit le nombre de fiches d'œuvres affichées sur chaque page du document")}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="1">
                                      {vt("onePerPage", "Une fiche par page")}
                                    </SelectItem>
                                    <SelectItem value="3">
                                      {vt("threePerPage", "Trois fiches par page")}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                        )}

                      {(template === "complete" || template === "simple") &&
                        selectedArtworksIds.length > 1 &&
                        hasThreePerPageOverflowRisk && (
                          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                            <p>
                              {vt(
                                "threePerPageOverflowWarning",
                                "Avec les options sélectionnées, une fiche risque de dépasser un tiers de page : les fiches seront réparties sur plus de pages que prévu. Décochez des champs ou passez à une fiche par page.",
                              )}
                            </p>
                          </div>
                        )}

                      {/* Vignettes obligatoires sur le modèle liste : plus de
                          case à cocher, showThumbnails reste toujours true. */}

                      {template === "list-text" && (
                        <div className="mt-2">
                          <FormLabel className="text-sm font-medium">
                            {vt("listSummaryFields", "Champs du résumé")}
                          </FormLabel>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {vt(
                              "listSummaryFieldsHint",
                              "Glissez pour réordonner, cochez pour afficher.",
                            )}
                          </p>
                          {(() => {
                            const LABELS: Record<string, string> = {
                              registrationNumber: "N° d'enregistrement",
                              artist: "Artiste",
                              title: "Titre",
                              creationDate: "Date de création",
                              dimensions: "Dimensions",
                              techniqueType: "Technique",
                              signature: "Signature",
                              status: "Statut",
                              price: "Prix de vente",
                              storage: "Rangement",
                            };
                            const order: string[] =
                              form.watch("summaryFieldsOrder") ??
                              Object.keys(LABELS);
                            const orderedKeys = order.filter((k) => k in LABELS);
                            const items = orderedKeys.map((k) => ({ id: k }));
                            return (
                              <Sortable
                                value={items}
                                onValueChange={(next) =>
                                  form.setValue(
                                    "summaryFieldsOrder",
                                    next.map((i) => String(i.id)),
                                  )
                                }
                              >
                                <div className="mt-2 flex flex-col gap-1">
                                  {orderedKeys.map((key) => (
                                    <SortableItem key={key} value={key} asChild>
                                      <div className="flex flex-row items-center gap-2 rounded-md border px-2 py-1">
                                        <SortableDragHandle
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 shrink-0"
                                        >
                                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        </SortableDragHandle>
                                        <FormField
                                          control={form.control}
                                          name={`summaryFields.${key}` as any}
                                          render={({ field }) => (
                                            <FormItem className="flex flex-row items-center gap-2 space-y-0">
                                              <FormControl>
                                                <Checkbox
                                                  checked={field.value as boolean}
                                                  onCheckedChange={
                                                    field.onChange
                                                  }
                                                />
                                              </FormControl>
                                              <FormLabel className="text-sm font-normal cursor-pointer">
                                                {vt(
                                                  `listSummaryField.${key}`,
                                                  LABELS[key],
                                                )}
                                              </FormLabel>
                                            </FormItem>
                                          )}
                                        />
                                      </div>
                                    </SortableItem>
                                  ))}
                                </div>
                              </Sortable>
                            );
                          })()}
                          {/* Pouces / centimètres : exclusifs, sinon les
                              dimensions se retrouveraient vides. */}
                          <FormField
                            control={form.control}
                            name="summaryFields.hideInches"
                            render={({ field }) => (
                              <FormItem className="mt-3 flex flex-row items-center gap-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value as boolean}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (checked) {
                                        form.setValue(
                                          "summaryFields.hideCentimeters",
                                          false,
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {vt(
                                    "listSummaryField.hideInches",
                                    "Masquer les pouces",
                                  )}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="summaryFields.hideCentimeters"
                            render={({ field }) => (
                              <FormItem className="mt-2 flex flex-row items-center gap-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value as boolean}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (checked) {
                                        form.setValue(
                                          "summaryFields.hideInches",
                                          false,
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {vt(
                                    "listSummaryField.hideCentimeters",
                                    "Masquer les centimètres",
                                  )}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="summaryFields.hideRegistrationLabel"
                            render={({ field }) => (
                              <FormItem className="mt-2 flex flex-row items-center gap-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value as boolean}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {vt(
                                    "listSummaryField.hideRegistrationLabel",
                                    "Masquer le label « N° d'enregistrement »",
                                  )}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        </div>
                      )}

                      {/* Editable document title (replaces the title toggle) */}
                      <FormField
                        control={form.control}
                        name="listTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-normal">
                              {vt("printDocumentTitleLabel", "Titre du document")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ""}
                                placeholder={
                                  vt("printDocumentTitlePlaceholder", "Ex. Sélection expo X")
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Show pagination checkbox */}
                      <FormField
                        control={form.control}
                        name="showPagination"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt("showPagination", "Afficher la pagination")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />

                      {/* Show print date checkbox */}
                      <FormField
                        control={form.control}
                        name="showPrintDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt("showPrintDate", "Afficher la date d'impression")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="preview" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("printPreviewTitle", "Prévisualisation")}
                </h3>
                {/* Rappel : couverture saisie mais non enregistrée comme modèle (#13) */}
                {(() => {
                  if (!hasUnsavedCover) return null;
                  return (
                    <div className="mb-3 flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                        <span>
                          {vt(
                            "coverPageUnsavedHintPreview",
                            "Votre page de couverture n'est pas enregistrée comme modèle. Elle s'imprime bien, mais enregistrez-la pour la réutiliser.",
                          )}
                        </span>
                      </div>
                      <div className="flex justify-end">
                        {showSaveInput ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={saveModelName}
                              onChange={(e) => setSaveModelName(e.target.value)}
                              placeholder={t("coverPageModelName")}
                              className="h-8 flex-1 bg-white text-sm"
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleSaveCover()
                              }
                            />
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSaveCover}
                              disabled={!saveModelName.trim() || isSavingCover}
                            >
                              <Save className="size-3.5 mr-1.5" />
                              {t("save")}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowSaveInput(false);
                                setSaveModelName("");
                              }}
                            >
                              {t("cancel")}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="bg-white"
                            onClick={() => setShowSaveInput(true)}
                          >
                            <Save className="size-3.5 mr-1.5" />
                            {vt("saveCoverAsModel", "Enregistrer comme modèle")}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* PDF Preview */}
                <div className="flex justify-center overflow-auto max-h-[calc(100vh-350px)] border rounded-lg bg-gray-50 p-4">
                  {isGeneratingPreview ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {vt("generatingPreview", "Génération de l'aperçu...")}
                      </span>
                    </div>
                  ) : previewError ? (
                    <div className="flex flex-col items-center justify-center gap-4 py-12">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                      <span className="text-sm text-destructive">
                        {previewError}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => generatePreview(true)}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        {vt("retry", "Réessayer")}
                      </Button>
                    </div>
                  ) : previewURL ? (
                    <PDFViewer fileURL={previewURL} />
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <span className="text-sm text-muted-foreground">
                        {vt("clickToGeneratePreview", "Cliquez sur l'onglet pour générer l'aperçu")}
                      </span>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

/**
 * Le calcul de présence est fourni au-dessus du drawer : toutes les listes de
 * cases, y compris « Tout cocher », lisent la même réponse.
 */
const ArtworkPrintDrawer = (props: ArtworkPrintDrawerProps) => (
  <FieldPresenceProvider table="artwork" ids={props.selectedArtworksIds}>
    <ArtworkPrintDrawerInner {...props} />
  </FieldPresenceProvider>
);

export default ArtworkPrintDrawer;
